// WorkingTogetherSplit.jsx
import React, { useLayoutEffect, useRef } from "react";
import "./WorkingTogetherSplit.css";

/**
 * Working Together – Split layout (left content, right image)
 * - Desktop: right image matches left content height (JS sync).
 * - ≤1024px: stacks; image uses a stable aspect-ratio (no stretching).
 */
export default function WorkingTogetherSplit({
  imageSrc = `${process.env.PUBLIC_URL}/Appraisal.jpg`,
  imageAlt = "Newer-construction Craftsman-style home in Washington State",
}) {
  const contentRef = useRef(null);
  const frameRef = useRef(null);

  // Sync media frame height to the content height on desktop only
  useLayoutEffect(() => {
    const contentEl = contentRef.current;
    const frameEl = frameRef.current;
    if (!contentEl || !frameEl) return;

    const setHeights = () => {
      const isStacked = window.matchMedia("(max-width: 1024px)").matches;
      if (isStacked) {
        // let CSS control size on small screens
        frameEl.style.height = "";
        frameEl.style.minHeight = "";
        return;
      }
      const h = contentEl.offsetHeight;
      frameEl.style.height = `${h}px`;
      frameEl.style.minHeight = `${h}px`;
    };

    setHeights();

    let ro;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(setHeights);
      ro.observe(contentEl);
    } else {
      window.addEventListener("resize", setHeights);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", setHeights);
    };
  }, []);

  return (
    <section className="WorkingTogether" aria-labelledby="wt-title">
      <div className="WorkingTogether__container">
        {/* LEFT: Content */}
        <div className="WorkingTogether__content" ref={contentRef}>
          <p className="WorkingTogether__eyebrow">Working Together</p>

          <h2 id="wt-title" className="WorkingTogether__title">
            A smoother process for you
          </h2>

          <p className="WorkingTogether__body">
            We take interior and exterior photos, measure the home per ANSI
            standards, and note features, updates, and overall condition. Most
            inspections take 30 to 60 minutes depending on the property. From the
            first call, we outline what we'll need—access details, known
            upgrades, permits, HOA info (if applicable), and any unique
            circumstances. After you receive the report, we remain available to
            answer questions, provide high-level explanations for stakeholders,
            and suggest what documentation could strengthen a future review.
          </p>

          <ul
            className="WorkingTogether__list"
            aria-label="What we'll typically request"
          >
            <li className="WorkingTogether__item">
              <span className="WorkingTogether__icon" aria-hidden="true">
                {checkIcon()}
              </span>
              <span>Access details & scheduling preferences</span>
            </li>
            <li className="WorkingTogether__item">
              <span className="WorkingTogether__icon" aria-hidden="true">
                {checkIcon()}
              </span>
              <span>Known upgrades, permits, and unique circumstances</span>
            </li>
            <li className="WorkingTogether__item">
              <span className="WorkingTogether__icon" aria-hidden="true">
                {checkIcon()}
              </span>
              <span>HOA information (if applicable)</span>
            </li>
          </ul>
        </div>

        {/* RIGHT: Image */}
        <div className="WorkingTogether__media">
          <figure className="WorkingTogether__frame" ref={frameRef}>
            <img
              className="WorkingTogether__img"
              src={imageSrc}
              alt={imageAlt}
              loading="lazy"
              decoding="async"
            />
            <div className="WorkingTogether__image-overlay" aria-hidden="true"></div>
            <figcaption className="WorkingTogether__caption">
              Clear steps. Respect for your time.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* Inline SVG */
function checkIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill="currentColor" opacity=".14" />
      <path
        d="M6.5 10.5l2.2 2.2 4.8-4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
